import { TokenQuantity } from './chunk-OKLM3BVS.js';
import { BatchItems } from './chunk-CPO2BA7M.js';
import { ProtocolBadge } from './chunk-UZVB5PVG.js';
import { fieldAtPath, SectionCard, ProtocolValue, FactGrid, ProtocolDisclosure, ProtocolParamList, decodedOrRaw, legField } from './chunk-KQLD5VJQ.js';
import { rawOf } from '@authe/fe-commons/storage';
import { jsx, jsxs } from 'react/jsx-runtime';

var field = (label, path, extra = {}) => ({ label, paths: [path], ...extra });
var count = (label, path) => field(label, path, { count: true });
var CALL_FIELDS = {
  shield: [count("Submitted shield notes", "shieldRequests")],
  transact: [count("Submitted transactions", "transactions")],
  relay: [count("Submitted transactions", "transactions"), count("Requested external calls", "actionData.calls"), field("Require every call to succeed", "actionData.requireSuccess")],
  multicall: [count("Requested external calls", "calls"), field("Require every call to succeed", "requireSuccess")],
  transfer: [count("Requested transfers", "transfers")],
  wrapBase: [field("Requested amount", "amount", { quantity: true })],
  unwrapBase: [field("Requested amount", "amount", { quantity: true })]
};
var EVENT_FIELDS = {
  Shield: [field("Merkle tree", "treeNumber"), field("First leaf position", "startPosition"), count("Public shield notes", "commitments")],
  Unshield: [field("Public recipient", "to"), field("Token", "token.tokenAddress"), field("Token type", "token.tokenType"), field("Token ID", "token.tokenSubID"), field("Public payout", "amount", { quantity: true }), field("Protocol fee", "fee", { quantity: true })],
  Transact: [field("Merkle tree", "treeNumber"), field("First leaf position", "startPosition"), count("Output commitments", "hash")],
  Nullified: [field("Merkle tree", "treeNumber"), count("Spent nullifiers", "nullifier")],
  CallError: [field("Failed call index (zero-based)", "callIndex")]
};
function factsFor(params, fields) {
  const tokenType = String(rawOf(fieldAtPath(params, "token.tokenType")));
  return fields.flatMap((spec) => {
    const value = fieldAtPath(params, ...spec.paths);
    if (value == null) return [];
    if (spec.count) {
      const items = decodedOrRaw(value);
      return Array.isArray(items) ? [{ label: spec.label, value: String(items.length) }] : [];
    }
    return [{ label: spec.label, value: spec.quantity ? /* @__PURE__ */ jsx(TokenQuantity, { value, fungible: tokenType !== "1" && tokenType !== "2" }) : /* @__PURE__ */ jsx(ProtocolValue, { value }) }];
  });
}
function RailgunCard({ title, address, params, fields, note, event = false, children }) {
  const facts = factsFor(params, fields);
  return /* @__PURE__ */ jsxs(SectionCard, { title: /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: title }), protocolId: "railgun", meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: "railgun" }), children: [
    address && /* @__PURE__ */ jsxs("p", { className: "mb-2 flex min-w-0 flex-wrap gap-1 text-xs", children: [
      event ? "Emitted by" : "Contract",
      " ",
      /* @__PURE__ */ jsx(ProtocolValue, { value: address, type: "address" })
    ] }),
    /* @__PURE__ */ jsx(FactGrid, { facts }),
    children,
    /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs text-muted-foreground", children: note }),
    facts.length ? /* @__PURE__ */ jsx(ProtocolDisclosure, { className: "mt-3 min-w-0", summary: event ? "All event fields" : "All call arguments", children: /* @__PURE__ */ jsx(ProtocolParamList, { params, emptyLabel: "No decoded fields" }) }) : /* @__PURE__ */ jsx(ProtocolParamList, { params, emptyLabel: "No decoded fields" })
  ] });
}
function ShieldNotes({ params }) {
  const notes = decodedOrRaw(fieldAtPath(params, "commitments"));
  const fees = decodedOrRaw(fieldAtPath(params, "fees"));
  if (!Array.isArray(notes) || !notes.length) return null;
  return /* @__PURE__ */ jsx("div", { className: "mt-3", children: /* @__PURE__ */ jsx(BatchItems, { items: notes, noun: "Public note", renderItem: (note, index) => {
    const token = legField(note, "token");
    const tokenType = String(rawOf(legField(token, "tokenType")));
    const tokenAddress = legField(token, "tokenAddress");
    const tokenId = legField(token, "tokenSubID");
    const value = legField(note, "value");
    const fee = Array.isArray(fees) ? fees[index] : void 0;
    const fungible = tokenType !== "1" && tokenType !== "2";
    return /* @__PURE__ */ jsx(FactGrid, { facts: [
      { label: "Token", value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenAddress, type: "address" }), when: tokenAddress != null },
      { label: "Token ID", value: /* @__PURE__ */ jsx(ProtocolValue, { value: tokenId }), when: !fungible && tokenId != null },
      { label: "Shielded principal", value: /* @__PURE__ */ jsx(TokenQuantity, { value, fungible }), when: value != null },
      { label: "Protocol fee", value: /* @__PURE__ */ jsx(TokenQuantity, { value: fee, fungible }), when: fee != null }
    ] });
  } }) });
}
function RailgunCall({ call, hint }) {
  const wrap = hint.call === "wrapBase" || hint.call === "unwrapBase";
  const amount = rawOf(fieldAtPath(call.params, "amount"));
  const allBalance = wrap && (amount === 0 || amount === "0" || amount === 0n);
  return /* @__PURE__ */ jsx(
    RailgunCard,
    {
      title: hint.label ?? call.label ?? "RAILGUN call",
      address: call.address,
      params: call.params,
      fields: CALL_FIELDS[hint.call ?? ""] ?? [],
      note: "Submitted arguments describe a request. Receipts and internal execution establish its outcome. The submitter or Relay Adapt contract does not identify a shielded owner.",
      children: allBalance && /* @__PURE__ */ jsxs("p", { className: "mt-2 text-xs", children: [
        "Zero requests the adapter's entire ",
        hint.call === "unwrapBase" ? "wrapped native" : "native",
        " balance. It does not mean zero tokens moved."
      ] })
    }
  );
}
function RailgunEvent({ event, hint }) {
  const note = event.name === "CallError" ? "The adapter reported this external call failed. This receipt alone does not mean the enclosing transaction reverted; inspect the internal execution and decoded failure bytes." : event.name === "Unshield" ? "The public payout excludes the separate protocol fee. This receipt does not identify a private deposit or prove ownership of the public recipient." : "Commitments and nullifiers are public receipt identifiers. They do not reveal shielded owners or private input\u2013output links. Note counts are not token amounts.";
  return /* @__PURE__ */ jsx(
    RailgunCard,
    {
      title: hint.label ?? event.name ?? "RAILGUN event",
      address: event.address,
      params: event.params,
      fields: EVENT_FIELDS[event.name ?? ""] ?? [],
      note,
      event: true,
      children: event.name === "Shield" && /* @__PURE__ */ jsx(ShieldNotes, { params: event.params })
    }
  );
}

// src/protocols/railgun/index.ts
var RAILGUN_CALL_SIGNATURES = {
  shield: "shield(((bytes32,(uint8,address,uint256),uint120),(bytes32[3],bytes32))[])",
  transact: "transact((((uint256,uint256),(uint256[2],uint256[2]),(uint256,uint256)),bytes32,bytes32[],bytes32[],(uint16,uint72,uint8,uint64,address,bytes32,(bytes32[4],bytes32,bytes32,bytes,bytes)[]),(bytes32,(uint8,address,uint256),uint120))[])",
  relay: "relay((((uint256,uint256),(uint256[2],uint256[2]),(uint256,uint256)),bytes32,bytes32[],bytes32[],(uint16,uint72,uint8,uint64,address,bytes32,(bytes32[4],bytes32,bytes32,bytes,bytes)[]),(bytes32,(uint8,address,uint256),uint120))[],(bytes31,bool,uint256,(address,bytes,uint256)[]))"
};
var RAILGUN_HINTS = {
  valueHints: [],
  callHints: [
    ["shield", "RAILGUN shield request"],
    ["transact", "RAILGUN transaction submission"],
    ["relay", "RAILGUN Relay Adapt request"],
    ["multicall", "RAILGUN Relay Adapt calls"],
    ["transfer", "RAILGUN Relay Adapt transfers"],
    ["wrapBase", "RAILGUN wrap native asset"],
    ["unwrapBase", "RAILGUN unwrap native asset"]
  ].map(([call, label]) => ({ protocolId: "railgun", call, signature: RAILGUN_CALL_SIGNATURES[call], label, component: "RailgunCall", summaryFields: [] })),
  eventHints: [
    ["Shield", "RAILGUN shield receipt"],
    ["Unshield", "RAILGUN unshield receipt"],
    ["Transact", "RAILGUN commitment receipt"],
    ["Nullified", "RAILGUN nullifier receipt"],
    ["CallError", "RAILGUN Relay Adapt call failure"]
  ].map(([event, label]) => ({ protocolId: "railgun", event, label, component: "RailgunEvent", summaryFields: [] }))
};
function registerRailgun(registry) {
  registry.registerCall("RailgunCall", RailgunCall);
  registry.registerEvent("RailgunEvent", RailgunEvent);
  registry.registerHints(RAILGUN_HINTS);
  return registry;
}

export { RAILGUN_CALL_SIGNATURES, RAILGUN_HINTS, registerRailgun };
//# sourceMappingURL=chunk-FVNHIQ5B.js.map
//# sourceMappingURL=chunk-FVNHIQ5B.js.map